import { useRef, useState, type ChangeEvent, type DragEvent } from 'react'
import { Navigate } from 'react-router-dom'
import { Download, Upload, AlertTriangle, CheckCircle2, Loader2, FileArchive } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { backupAPI } from '@/lib/api-client'
import { useRole } from '@/hooks/usePermission'

interface ImportResult {
  restored: Record<string, number>
  files: number
}

export function BackupPage() {
  const role = useRole()
  const [isExporting, setIsExporting] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const [importResult, setImportResult] = useState<ImportResult | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  if (!['ROOT', 'MASTER'].includes(role ?? '')) {
    return <Navigate to="/" replace />
  }

  const handleExport = async () => {
    setIsExporting(true)
    try {
      const { blob, filename } = await backupAPI.export()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)
      toast.success('Backup exportado com sucesso')
    } catch (err: any) {
      toast.error(err.message || 'Erro ao exportar backup')
    } finally {
      setIsExporting(false)
    }
  }

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.name.endsWith('.zip')) {
      toast.error('Selecione um arquivo .zip de backup')
      return
    }
    handleImport(file)
    e.target.value = ''
  }

  const handleImport = async (file: File) => {
    const confirmed = window.confirm(
      `Importar backup "${file.name}"?\n\nRegistros existentes com os mesmos IDs não serão sobrescritos. Novos registros serão adicionados.\n\nContinuar?`
    )
    if (!confirmed) return

    setIsImporting(true)
    setImportResult(null)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const result = await backupAPI.import(formData)
      setImportResult(result)
      toast.success('Backup importado com sucesso')
    } catch (err: any) {
      toast.error(err.message || 'Erro ao importar backup')
    } finally {
      setIsImporting(false)
    }
  }

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    const file = e.dataTransfer?.files?.[0]
    if (!file) return
    if (!file.name.endsWith('.zip')) {
      toast.error('Selecione um arquivo .zip de backup')
      return
    }
    handleImport(file)
  }

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-neutral-900">Backup</h1>
        <p className="text-sm text-neutral-500 mt-1">
          Exporte todos os dados do tenant em um arquivo portável ou restaure a partir de um backup existente.
        </p>
      </div>

      <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
        <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
        <div className="text-sm text-amber-800">
          <p className="font-medium">Atenção</p>
          <p>
            O backup inclui todos os dados do seu tenant (projetos, financeiro, fornecedores, etc.) e os
            arquivos enviados. Use para migrar para outro servidor ou manter uma cópia de segurança.
          </p>
        </div>
      </div>

      {/* Export */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Download className="h-4 w-4" />
            Exportar Backup
          </CardTitle>
          <CardDescription>
            Baixa um arquivo <code>.zip</code> contendo todos os dados e arquivos do seu tenant.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={handleExport} disabled={isExporting}>
            {isExporting ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Gerando backup...</>
            ) : (
              <><Download className="mr-2 h-4 w-4" />Baixar Backup Completo</>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Import */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Upload className="h-4 w-4" />
            Restaurar Backup
          </CardTitle>
          <CardDescription>
            Importa dados a partir de um arquivo <code>.zip</code> gerado anteriormente. Registros com o
            mesmo ID não serão sobrescritos; novos registros serão adicionados.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div
            className="rounded-lg border-2 border-dashed border-neutral-300 p-8 text-center cursor-pointer hover:border-neutral-400 hover:bg-neutral-50 transition-colors"
            onClick={() => fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
          >
            {isImporting ? (
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="h-8 w-8 text-neutral-400 animate-spin" />
                <p className="text-sm text-neutral-600">Importando dados...</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <FileArchive className="h-8 w-8 text-neutral-400" />
                <p className="text-sm font-medium text-neutral-700">
                  Arraste o arquivo <code>.zip</code> aqui ou clique para selecionar
                </p>
                <p className="text-xs text-neutral-500">Apenas arquivos de backup do EnlevoHub</p>
              </div>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".zip"
            className="hidden"
            onChange={handleFileSelect}
          />

          {importResult && (
            <div className="rounded-lg border border-green-200 bg-green-50 p-4 space-y-2">
              <div className="flex items-center gap-2 text-green-800">
                <CheckCircle2 className="h-4 w-4 shrink-0" />
                <p className="text-sm font-medium">Backup importado com sucesso</p>
              </div>
              <div className="text-xs text-green-700 space-y-1 pl-6">
                {Object.entries(importResult.restored)
                  .filter(([, count]) => Number(count) > 0)
                  .map(([key, count]) => (
                    <p key={key}>
                      {key}: <span className="font-medium">{Number(count)}</span> registros
                    </p>
                  ))}
                {importResult.files > 0 && (
                  <p>arquivos: <span className="font-medium">{importResult.files}</span></p>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
